// src/components/EmailVerificationWaiting.jsx
import { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Stack,
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";

function useInAppBrowser() {
  return useMemo(() => {
    const ua = (navigator.userAgent || "").toLowerCase();
    const ref = (document.referrer || "").toLowerCase();
    const haystack = ua + " " + ref;

    const inApp =
      /(instagram|fb_iab|fbav|facebook|messenger|line|micromessenger|wechat|weibo|qq|snapchat|pinterest|tiktok|bytedance|musical\.ly|lemon8|ttwebview|twitter)/i.test(
        haystack
      );

    return { inApp };
  }, []);
}

export default function EmailVerificationWaiting() {
  const navigate = useNavigate();
  const auth = getAuth();
  const { inApp } = useInAppBrowser();

  const [user, setUser] = useState(() => auth.currentUser || null);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [checking, setChecking] = useState(true);

  const pollRef = useRef(null);

  const pendingEmail = (() => {
    try {
      return localStorage.getItem("pendingEmail") || user?.email || "";
    } catch {
      return user?.email || "";
    }
  })();

  const handleStartOver = async () => {
    try {
      localStorage.removeItem("waitingForVerification");
      localStorage.removeItem("pendingEmail");
      localStorage.removeItem("postVerifyBypass");
    } catch {}

    try {
      await auth.signOut();
    } catch {}

    navigate("/signup", { replace: true });
  };

  // Auth watcher
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);

      if (u?.emailVerified) {
        try {
          localStorage.removeItem("waitingForVerification");
        } catch {}

        navigate("/create-event", { replace: true });
        return;
      }

      try {
        if (u && localStorage.getItem("postVerifyBypass") === "1") {
          localStorage.removeItem("postVerifyBypass");
          localStorage.removeItem("waitingForVerification");

          navigate("/create-event", { replace: true });

          u.reload().catch(() => {});
          u.getIdToken?.(true).catch(() => {});

          return;
        }
      } catch {}

      setChecking(false);
    });

    return () => unsub();
  }, [auth, navigate]);

  // Resend timer
  useEffect(() => {
    if (timer > 0) {
      const id = setTimeout(() => setTimer((t) => t - 1), 1000);
      return () => clearTimeout(id);
    }

    setCanResend(true);
  }, [timer]);

  // Poll verification when signed in
  useEffect(() => {
    if (!user) return;

    const poll = async () => {
      try {
        await user.reload();
      } catch {}

      if (user.emailVerified) {
        try {
          localStorage.removeItem("waitingForVerification");
        } catch {}

        navigate("/create-event", { replace: true });
        return;
      }

      pollRef.current = setTimeout(poll, 500);
    };

    poll();

    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [user, navigate]);

  const handleResend = async () => {
    setResendError(null);

    if (!auth.currentUser) {
      setResendError("No authenticated user found. Please sign in to resend.");
      return;
    }

    try {
      const fns = getFunctions(undefined, "us-central1");
      const fn = httpsCallable(fns, "resendVerificationEmail");

      await fn();

      setCanResend(false);
      setTimer(60);
    } catch (err) {
      setResendError(
        err?.message || "Failed to resend verification email."
      );
    }
  };

  const handleCheckAgain = async () => {
    if (!auth.currentUser) return;

    setChecking(true);

    try {
      await auth.currentUser.reload();
    } catch {}

    setChecking(false);

    if (auth.currentUser.emailVerified) {
      try {
        localStorage.removeItem("waitingForVerification");
      } catch {}

      navigate("/create-event", { replace: true });
    }
  };

  return (
    <Box textAlign="center" sx={{ mt: 6, px: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        Verify your email to continue
      </Typography>

      {pendingEmail ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          We sent a link to <strong>{pendingEmail}</strong>.
        </Typography>
      ) : null}

     {inApp ? (
  <Typography variant="body2" sx={{ mt: 1.5 }}>
    Verify your email, then come back here.
  </Typography>
) : (
  <>
    <Typography variant="body2">
      Once you verify, we'll continue automatically. Didn't get it? Check
      spam/junk.
    </Typography>

    <Typography variant="body2" sx={{ mt: 1.25 }}>
      You can leave this page open and return after verifying.
    </Typography>
  </>
)}

      {checking ? <CircularProgress size={24} sx={{ mt: 3 }} /> : null}

      {user ? (
        <>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
            sx={{ mt: 2, flexWrap: "wrap" }}
          >
            <Button
              variant="outlined"
              onClick={handleCheckAgain}
              disabled={checking}
            >
              I verified — Check again
            </Button>

            <Button
              variant="contained"
              onClick={handleResend}
              disabled={!canResend}
            >
              {canResend ? "Resend email" : `Resend in ${timer}s`}
            </Button>
          </Stack>

          {resendError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              ❌ {resendError}
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ mt: 2 }}>
            You're not signed in in this browser.
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate("/signin")}
            sx={{ mt: 1.25, minWidth: 240 }}
          >
            Sign in to continue
          </Button>

          {inApp && (
            <Button
              variant="text"
              onClick={handleStartOver}
              sx={{ mt: 1, display: "block", mx: "auto" }}
            >
              Start over
            </Button>
          )}
        </>
      )}
    </Box>
  );
}